/**
 * PipelineBoard - Kanban board container.
 * Renders 5 columns (Saved, Applied, Interview, Offer, Rejected) horizontally.
 * Uses @dnd-kit/core for pointer + touch drag-and-drop support.
 */

import { useCallback, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import type { PipelineJob, PipelineStatus } from '@/types/pipeline';
import { PIPELINE_COLUMNS } from '@/types/pipeline';
import { PipelineColumn } from './PipelineColumn';
import { PipelineCard } from './PipelineCard';

interface PipelineBoardProps {
  jobs: PipelineJob[];
  onMoveJob: (jobId: string, newStatus: PipelineStatus) => void;
  onDeleteJob: (jobId: string) => void;
}

/** Status progression for "move to next" quick action */
const NEXT_STATUS: Record<PipelineStatus, PipelineStatus | null> = {
  saved: 'applied',
  applied: 'interview',
  interview: 'offer',
  offer: null,
  rejected: null,
};

export function PipelineBoard({ jobs, onMoveJob, onDeleteJob }: PipelineBoardProps) {
  const [activeJob, setActiveJob] = useState<PipelineJob | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require 5px movement before drag starts — lets clicks pass through
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      // Long-press 250ms activates drag; 10px tolerance for finger jitter
      activationConstraint: { delay: 250, tolerance: 10 },
    })
  );

  /** Disable scroll-snap during drag so columns don't jerk under the finger */
  const disableScrollSnap = useCallback(() => {
    boardRef.current?.style.setProperty('scroll-snap-type', 'none');
  }, []);

  const restoreScrollSnap = useCallback(() => {
    boardRef.current?.style.removeProperty('scroll-snap-type');
  }, []);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const job = jobs.find((j) => j.id === event.active.id);
      setActiveJob(job ?? null);
      disableScrollSnap();
    },
    [jobs, disableScrollSnap]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        onMoveJob(active.id as string, over.id as PipelineStatus);
      }
      setActiveJob(null);
      restoreScrollSnap();
    },
    [onMoveJob, restoreScrollSnap]
  );

  const handleDragCancel = useCallback(() => {
    setActiveJob(null);
    restoreScrollSnap();
  }, [restoreScrollSnap]);

  const handleMoveToNext = useCallback(
    (jobId: string) => {
      const job = jobs.find((j) => j.id === jobId);
      if (!job) return;
      const next = NEXT_STATUS[job.status];
      if (next) {
        onMoveJob(jobId, next);
      }
    },
    [jobs, onMoveJob]
  );

  /** Group jobs by their status for each column */
  const getJobsForStatus = useCallback(
    (status: PipelineStatus): PipelineJob[] => {
      return jobs.filter((job) => job.status === status);
    },
    [jobs]
  );

  /* noop handlers for the overlay card — it's purely visual */
  const noop = useCallback(() => {}, []);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="pipeline-board" ref={boardRef}>
        {PIPELINE_COLUMNS.map((column) => (
          <PipelineColumn
            key={column.id}
            id={column.id}
            title={column.title}
            color={column.color}
            jobs={getJobsForStatus(column.id)}
            onMoveToNext={handleMoveToNext}
            onDelete={onDeleteJob}
          />
        ))}
      </div>

      {/* Drag overlay — renders the dragged card in a portal above everything */}
      <DragOverlay dropAnimation={{ duration: 150, easing: 'ease' }}>
        {activeJob ? (
          <PipelineCard
            job={activeJob}
            onMoveToNext={noop}
            onDelete={noop}
            isOverlay
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
